/**
 * Spec 014b US-4, US-5, FR-018: the "Booking rules" admin card (minimum age,
 * one daily rate, deposit collection and release; spec 015 recall check; spec 013 P7 cardholder
 * name check; spec 022 car card price). Plain functions, tested in `carSettingsAdmin.test.ts`.
 */

import { CarCardPriceMode, carCardPriceModeOf } from '../../utils/carCardPrice';
import { describeBookingsOpenValue } from './bookingsOpenAdmin';

export interface BookingRulesForm {
  /** Blank = 18. */
  minimumAge: string;
  uniformDailyRate: boolean;
  /** Spec 015: cars need a recall check on record before guests can book them here. */
  requireRecallCheck: boolean;
  /**
   * Spec 013 P7 (FR-033): refuse a card whose name is not the guest's. Off = charge it anyway and
   * tell the host and brand admins. On when nothing is stored.
   */
  requireCardholderNameMatch: boolean;
  depositCollection: 'authorize' | 'charge';
  /** Blank = 80 hours after the trip. */
  depositReleaseBusinessDays: string;
  /** Spec 022 D8: guest car cards show the daily price (default) or the week price. */
  carCardPrice: CarCardPriceMode;
}

type Rules = Record<string, unknown> | null | undefined;

const text = (value: unknown) => (value === null || value === undefined ? '' : String(value));

export function toBookingRulesForm(rules: Rules): BookingRulesForm {
  return {
    minimumAge: text(rules?.minimumAge),
    uniformDailyRate: rules?.uniformDailyRate === true,
    requireRecallCheck: rules?.requireRecallCheck === true,
    requireCardholderNameMatch: rules?.requireCardholderNameMatch !== false,
    depositCollection: rules?.depositCollection === 'charge' ? 'charge' : 'authorize',
    depositReleaseBusinessDays: text(rules?.depositReleaseBusinessDays),
    carCardPrice: carCardPriceModeOf(rules?.carCardPrice as string | undefined),
  };
}

/** Spec 022: words for the history list ("Car card price: Daily → Weekly"). */
export const CAR_CARD_PRICE_LABELS: Record<CarCardPriceMode, string> = {
  daily: 'Daily',
  weekly: 'Weekly',
  monthly: 'Monthly',
};

/** History words for booking rules that are not plain values; null = not one of them. */
export function describeBookingRuleValue(key: string, value: unknown): string | null {
  if (key === 'carCardPrice') {
    return CAR_CARD_PRICE_LABELS[carCardPriceModeOf(value as string | undefined)];
  }
  if (key === 'bookingsOpen') {
    return describeBookingsOpenValue(value);
  }
  return null;
}

function whole(raw: string, min: number, max: number): { value: number | null; error?: string } {
  const trimmed = raw.trim();
  if (!trimmed) return { value: null };
  const number = Number(trimmed);
  if (!Number.isInteger(number)) return { value: null, error: 'Enter a whole number' };
  if (number < min) return { value: null, error: `At least ${min}` };
  if (number > max) return { value: null, error: `At most ${max}` };
  return { value: number };
}

/** Only the changed rules; errors keyed by rule. */
export function bookingRulesPatch(
  rules: Rules,
  form: BookingRulesForm,
): { patch: Record<string, unknown>; errors: Partial<Record<keyof BookingRulesForm, string>> } {
  const errors: Partial<Record<keyof BookingRulesForm, string>> = {};
  const age = whole(form.minimumAge, 16, 99);
  if (age.error) errors.minimumAge = age.error;
  const release = whole(form.depositReleaseBusinessDays, 0, 30);
  if (release.error) errors.depositReleaseBusinessDays = release.error;

  const next: Record<string, unknown> = {
    minimumAge: age.value,
    uniformDailyRate: form.uniformDailyRate,
    requireRecallCheck: form.requireRecallCheck,
    requireCardholderNameMatch: form.requireCardholderNameMatch,
    depositCollection: form.depositCollection,
    depositReleaseBusinessDays: release.value,
    carCardPrice: form.carCardPrice,
  };
  const loaded: Record<string, unknown> = {
    minimumAge: rules?.minimumAge ?? null,
    uniformDailyRate: rules?.uniformDailyRate === true,
    requireRecallCheck: rules?.requireRecallCheck === true,
    requireCardholderNameMatch: rules?.requireCardholderNameMatch !== false,
    depositCollection: rules?.depositCollection === 'charge' ? 'charge' : 'authorize',
    depositReleaseBusinessDays: rules?.depositReleaseBusinessDays ?? null,
    carCardPrice: carCardPriceModeOf(rules?.carCardPrice as string | undefined),
  };
  const patch: Record<string, unknown> = {};
  if (Object.keys(errors).length === 0) {
    for (const key of Object.keys(next)) {
      if (next[key] !== loaded[key]) patch[key] = next[key];
    }
  }
  return { patch, errors };
}
